import { useUserProfile } from "#webapp/src/components/hooks/useUserProfile";
import { CalendarProfile } from "#webapp/src/components/web-app-calendar/multi-calendar-select/util/MultiCalendarSelect.util";
import { Button } from "@clockwise/design-system";
import { fg_muted } from "@clockwise/design-system/tokens";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { generateScheduleCQL } from "@clockwise/web-commons/src/util/generateCQL";
import { Tooltip } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { compact, differenceWith, intersection, range } from "lodash";
import { Eye, PushPin, PushPinSlash } from "phosphor-react";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBoolean } from "usehooks-ts";
import {
  addCalendarIds,
  clearCalendarIds,
  removeCalendarIds,
} from "../../../../state/actions/multi-calendar.actions";
import { IReduxState } from "../../../../state/reducers/root.reducer";
import { TrackingEvents, track } from "../../../../util/analytics.util";
import { useReadCalendarColors } from "../../../web-app-calendar/hooks/CalendarColorsContext";
import { Collaborator, useCollaborators } from "../hooks/useCollaborators";
import { useProcessMessage } from "../hooks/useProcessMessage";
import { CollaboratorSearch } from "./CollaboratorSearch";

const ScheduleTimeAction = ({
  duration,
  onClick,
  nameOrEmail,
}: {
  duration: number;
  onClick: (e: React.MouseEvent) => void;
  nameOrEmail: string;
}) => {
  return (
    <Tooltip title={`Schedule ${duration} minutes with ${nameOrEmail}`}>
      <Button onClick={onClick} size="mini" variant="outlined">{`${duration}m`}</Button>
    </Tooltip>
  );
};

const PushPinOn = () => <PushPin size={16} fill="white" color={fg_muted} />;
const PushPinOff = () => <PushPinSlash size={16} fill="white" color={fg_muted} />;

const PinToggle = ({
  onClick,
  isPinned,
  fullName,
}: {
  onClick: (e: React.MouseEvent) => void;
  isPinned: boolean;
  fullName: string | null;
}) => {
  return (
    <Tooltip
      title={
        isPinned
          ? `Unpin ${fullName ? fullName : ""} from this list`
          : `Pin ${fullName ? fullName : ""} to this list`
      }
    >
      <Button
        onClick={onClick}
        size="mini"
        variant="outlined"
        startIcon={isPinned ? PushPinOff : PushPinOn}
      />
    </Tooltip>
  );
};

const CollaboratorCard = ({
  collaborator,
  isInMultiCalList,
  removeCallback,
  onPinToggle,
}: {
  collaborator: Collaborator;
  isInMultiCalList: boolean;
  removeCallback: (calId: string) => void;
  onPinToggle: (collaborator: Collaborator) => void;
}) => {
  const dispatch = useDispatch();
  const calendarColors = useReadCalendarColors();
  const { processMessage, canProcessMessage } = useProcessMessage();

  const calendarColor = calendarColors[collaborator.calendarId || ""]?.border;

  const { value: isFocused, setTrue: setFocused, setFalse: setNotFocused } = useBoolean(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const toggleUser = () => {
    if (!collaborator?.calendarId) return;
    track(TrackingEvents.CHAT.NUX.SIDEBAR_COLLABORATOR_VIEW_CALENDAR_CLICKED, {
      isAdding: !isInMultiCalList,
    });
    if (isInMultiCalList) {
      if (!collaborator.isPinned) {
        removeCallback(collaborator.calendarId);
      }
      dispatch(removeCalendarIds([collaborator.calendarId]));
    } else {
      dispatch(addCalendarIds([collaborator.calendarId]));
    }
  };

  const generateCQLForCollaborator = (duration: number) => {
    if (!collaborator?.calendarId) return;
    const name = collaborator.fullName || collaborator.calendarId;
    const cql = generateScheduleCQL([collaborator.calendarId], duration);
    track(TrackingEvents.CHAT.NUX.SIDEBAR_COLLABORATOR_QUICK_CREATE_CLICKED, { duration });
    void processMessage(
      `Schedule ${duration} minutes with ${name}`,
      {
        eventMentions: [],
        personMentions: [
          {
            calendarId: collaborator.calendarId,
            name: name,
          },
        ],
      },
      cql,
    );
  };

  // prevent ui flicker on tabbing
  const delayedSetNotfocused = useCallback(() => {
    setTimeout(() => {
      const focusWithin = containerRef.current?.contains(document.activeElement);
      if (!focusWithin) {
        setNotFocused();
      }
    }, 10);
  }, [setNotFocused]);

  let actions: React.JSX.Element[] = [];
  if (isFocused) {
    if (canProcessMessage) {
      actions = [
        <ScheduleTimeAction
          key={"time-action-15"}
          duration={15}
          nameOrEmail={collaborator.fullName || collaborator.calendarId}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            generateCQLForCollaborator(15);
          }}
        />,
        <ScheduleTimeAction
          key={"time-action-30"}
          duration={30}
          nameOrEmail={collaborator.fullName || collaborator.calendarId}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            generateCQLForCollaborator(30);
          }}
        />,
        <ScheduleTimeAction
          key={"time-action-60"}
          duration={60}
          nameOrEmail={collaborator.fullName || collaborator.calendarId}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            generateCQLForCollaborator(60);
          }}
        />,
        <PinToggle
          key={"togglecal-1"}
          isPinned={collaborator.isPinned}
          fullName={collaborator.fullName}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onPinToggle(collaborator);
          }}
        />,
      ];
    }
  }

  const possesiveName = collaborator.fullName ? `${collaborator.fullName}'s` : "";

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onMouseEnter={setFocused}
      onMouseLeave={setNotFocused}
      onBlur={delayedSetNotfocused}
      onFocus={setFocused}
      onClick={toggleUser}
      className="cw-flex cw-group cw-justify-between cw-items-center cw-rounded-lg cw-p-1 cw-cursor-pointer hover:cw-bg-neutral-inset"
    >
      <Tooltip
        title={
          isInMultiCalList ? `Hide ${possesiveName} calendar` : `View ${possesiveName} calendar`
        }
      >
        <div className="cw-flex cw-gap-3 cw-items-center cw-truncate">
          <AttendeeAvatar
            hideTooltip
            borderColor={isInMultiCalList ? calendarColor : undefined}
            calendarId={collaborator.calendarId || undefined}
            displayName={collaborator.fullName || undefined}
            profile={{
              externalImageUrl: collaborator.externalImageUrl,
            }}
            size="large"
          />
          <div className="cw-body-sm cw-font-medium">
            {collaborator.fullName || collaborator.calendarId}
          </div>
        </div>
      </Tooltip>
      <div className="group-hover:cw-visible"></div>
      {actions.length ? (
        <div className="cw-flex cw-gap-0.5">{actions.map((action) => action)}</div>
      ) : isInMultiCalList ? (
        <Eye color={calendarColor} weight="fill" size={16} />
      ) : null}
    </div>
  );
};

export const CustomCollaborators = ({
  topCollaborators,
  customCollaborators,
  loading,
}: {
  topCollaborators: Collaborator[];
  customCollaborators: Collaborator[];
  loading: boolean;
}) => {
  const [tempPersons, setTempPersons] = React.useState<Collaborator[]>([]);
  const { updateCollaborators } = useCollaborators();
  const { userProfile } = useUserProfile();
  const dispatch = useDispatch();

  const selectedCalendarIds = useSelector(
    (state: IReduxState) => state.multiCalendar.calendarIdsToFullyShow,
  );

  const pinnedCollaborators = [...tempPersons, ...customCollaborators];
  const remainingTopCollaborators = topCollaborators.filter(
    (top) => !pinnedCollaborators.some((custom) => custom.calendarId === top.calendarId),
  );

  useEffect(() => {
    // If thereare no custom collaborators, default to the top collaborators and pin them
    if (!customCollaborators.length && topCollaborators.length && !loading) {
      updateCollaborators(remainingTopCollaborators.slice(0, 6));
    }
  }, [remainingTopCollaborators.length, topCollaborators.length, loading]);

  useEffect(() => {
    // If the redux selected Ids change in count, that means that the user has removed a calendar from multi-cal.
    // This logic will remove it from our local state as well
    const tempPeronsIds = tempPersons.map((p) => p.calendarId);
    const differenceInSelectedIds = differenceWith(tempPeronsIds, selectedCalendarIds);
    if (differenceInSelectedIds.length) {
      const newTempPersons = tempPersons.filter(
        (p) => !differenceInSelectedIds.includes(p.calendarId),
      );
      setTempPersons(newTempPersons);
    }
  }, [selectedCalendarIds.length]);

  const allPersons = [...pinnedCollaborators, ...remainingTopCollaborators];

  if (!allPersons.length && !loading) return null;

  const collaboratorsAndPinnedPersonsAndYou = compact([
    userProfile.primaryCalendar,
    ...pinnedCollaborators.map((p) => p.calendarId),
  ]);

  const removeTempProfile = (calId: string) => {
    const tempCopy = [...tempPersons];
    const newTempPersons = tempCopy.filter((p) => p.calendarId !== calId);
    setTempPersons(newTempPersons);
  };

  const clearMultiCal = () => {
    dispatch(clearCalendarIds());
    setTempPersons([]);
  };

  const toggleCollabPin = (collaborator: Collaborator) => {
    const isCurrentlyPinned = customCollaborators.some(
      (c) => c.calendarId === collaborator.calendarId,
    );
    if (isCurrentlyPinned) {
      const newCustomCollaborators = customCollaborators.filter(
        (p) => p.calendarId !== collaborator.calendarId,
      );
      updateCollaborators(newCustomCollaborators);
      dispatch(removeCalendarIds([collaborator.calendarId]));
    } else {
      removeTempProfile(collaborator.calendarId);
      updateCollaborators([collaborator, ...customCollaborators]);
    }
  };

  const addTempProfile = (person: CalendarProfile) => {
    if (!person?.primaryCalendar) return;
    dispatch(addCalendarIds([person.primaryCalendar]));
    setTempPersons([
      {
        calendarId: person.primaryCalendar,
        sharedMinutes: null,
        isPinned: false,
        fullName: `${person.profile?.givenName} ${person.profile?.familyName}`,
        externalImageUrl: person.profile?.externalImageUrl || null,
      },
      ...tempPersons,
    ]);
  };

  const showHideCalendars = intersection(
    selectedCalendarIds,
    allPersons.map((p) => p.calendarId),
  );

  return (
    <div className="cw-rounded-lg cw-w-full cw-flex cw-flex-col cw-gap-1">
      <div className="cw-flex cw-justify-between cw-items-center cw-h-6">
        <div className="cw-body-base cw-text-12 cw-text-subtle cw-font-semibold">Collaborators</div>
        {!!showHideCalendars.length && (
          <Button onClick={clearMultiCal} size="mini" variant="text" sentiment="positive">
            Hide calendars
          </Button>
        )}
      </div>

      <CollaboratorSearch
        currentSelectedCalIds={collaboratorsAndPinnedPersonsAndYou}
        onSelect={addTempProfile}
        remainingTopCollaborators={remainingTopCollaborators.slice(0, 5)}
      />
      <div>
        {loading ? (
          range(6).map((_, index) => (
            <div
              key={`collab-loading-${index}`}
              className="cw-flex cw-gap-3 cw-items-center cw-my-1"
            >
              <Skeleton variant="circle" height={24} width={24} />
              <Skeleton variant="rect" height={24} className="cw-flex-1 cw-rounded-lg" />
            </div>
          ))
        ) : (
          <>
            {pinnedCollaborators.map((collaborator) => (
              <CollaboratorCard
                collaborator={collaborator}
                key={collaborator.calendarId}
                removeCallback={removeTempProfile}
                isInMultiCalList={selectedCalendarIds.includes(collaborator.calendarId || "")}
                onPinToggle={toggleCollabPin}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};
